import React, { useState, createContext } from 'react';
import PropTypes from 'prop-types';
import { isDate, omitBy, isEmpty, forEach } from 'lodash';
import { format } from 'date-fns';
import { useSession } from '@cordata/buffet';
import { object, string, mixed } from 'yup';

import {
  authorizedPyInstance,
} from '~/utils';

const PatientSearchContext = createContext({ results: undefined });

const INITIAL_VALUES = {
  firstName__like: '',
  lastName__like: '',
  birthDate: '',
  sourceMrns__contains: '',
  sourceDescription: [],
  agencyCounty: '',
  wkrName: '',
  sacwisPersonId: '',
  caseId: '',
  providerId: '',
  providerName: '',
};

// Match either a date in the format MM/DD/YYYY or a number two to four digits in length (i.e. an
// age or birth year)
const birthDateRegex = /^(?:(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/(19[0-9]{2}|20[0-9]{2})|\d{2,4})$/;

const VALIDATION_SCHEMA = object().shape({
  sourceDescription: string().when('sourceMrns__contains', {
    is: v => !!v,
    then: s => s.nullable().required(),
    otherwise: s => s.nullable().notRequired(),
  }),
  agencyCounty: mixed().when('wkrName', {
    is: v => !!v,
    then: s => s.nullable().required(),
    otherwise: s => s.nullable().notRequired(),
  }),
  birthDate: string().matches(birthDateRegex, 'Must be an age or birth year or a valid date in the format MM/DD/YYYY').nullable().notRequired(),
});

const DEFAULT_SORT = ['lastName', 'firstName'];

const buildQueryString = (params) => {
  const queries = [];

  queries.push(`sort=${DEFAULT_SORT.join(',')}`);

  forEach(omitBy(params, p => isEmpty(p) && !isDate(p)), (v, k) => {
    if (Array.isArray(v)) {
      queries.push(...v.map(vv => `${k}=${vv.value || vv}`));
    } else if (isDate(v)) {
      queries.push(`${k}=${format(v, 'MM/DD/YYYY')}`);
    } else if (k === 'agencyCounty') {
      queries.push(`${k}=${v.attributes.agencyCounty}`);
    } else {
      queries.push(`${k}=${v}`);
    }
  });

  return queries.join('&');
};

const PatientSearchProvider = ({ children }) => {
  const [results, setResults] = useState([]);
  const [status, setStatus] = useState();
  const { current } = useSession();
  const [hasSearched, setHasSearched] = useState(false);

  const searchPatients = (queryParams) => {
    setStatus('loading');

    const queryString = buildQueryString(queryParams);

    return authorizedPyInstance
      .get(`/api/data/public/identity_search?${queryString}`)
      .then((r) => {
        setStatus('success');
        setHasSearched(true);

        return r.data.data;
      });
  };

  const getRecentPatients = () => {
    setStatus('loading');

    return authorizedPyInstance
      .get('/api/data/public/list_view_cs_recent_patients', { params: { user_uid: current.attributes.userUid } })
      .then(r => r.data.data)
      .then((r) => {
        const patientUids = r.map(rr => rr.attributes.patientUid);

        if (patientUids.length) {
          searchPatients({ patientUid: patientUids })
            .then((x) => {
              setResults(x);

              setStatus('success');
            });
        } else {
          setResults([]);

          setStatus('success');
        }
      });
  };

  return (
    <PatientSearchContext.Provider
      value={{
        searchPatients,

        getRecentPatients,

        status,
        setStatus,

        results,
        setResults,

        initialValues: INITIAL_VALUES,
        validationSchema: VALIDATION_SCHEMA,

        hasSearched,
        setHasSearched,
      }}
    >
      {children}
    </PatientSearchContext.Provider>
  );
};

PatientSearchProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export { PatientSearchContext, PatientSearchProvider };
